import React, { useState, useRef, useEffect } from 'react';

const DatePicker = ({ 
  value, 
  onChange, 
  placeholder = "Select Date", 
  className = "form-control",
  disabled = false,
  required = false,
  maxDate = null,
  minDate = null
}) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const datePickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDisplayDate = (date) => {
    if (!date) return placeholder;
    const d = new Date(date);
    return d.toLocaleDateString('en-GB');
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const handleDateClick = (date) => {
    if (isDateDisabled(date)) return;
    const formattedDate = formatDate(date);
    onChange(formattedDate);
    setShowCalendar(false);
  };

  const isDateDisabled = (date) => {
    if (!date) return false;
    
    // Normalize dates to compare only date part (ignore time)
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    if (maxDate) {
      const maxDateObj = new Date(maxDate);
      const maxDateOnly = new Date(maxDateObj.getFullYear(), maxDateObj.getMonth(), maxDateObj.getDate());
      if (dateOnly > maxDateOnly) return true;
    }
    
    if (minDate) {
      const minDateObj = new Date(minDate);
      const minDateOnly = new Date(minDateObj.getFullYear(), minDateObj.getMonth(), minDateObj.getDate());
      if (dateOnly < minDateOnly) return true;
    }
    
    return false;
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date && 
           date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const isSelected = (date) => {
    if (!value || !date) return false;
    const selectedDate = new Date(value);
    return date.getDate() === selectedDate.getDate() &&
           date.getMonth() === selectedDate.getMonth() &&
           date.getFullYear() === selectedDate.getFullYear();
  };

  return (
    <div className="position-relative" ref={datePickerRef}>
      <div 
        className={`${className} d-flex justify-content-between align-items-center ${disabled ? 'disabled' : ''}`}
        style={{ cursor: disabled ? 'not-allowed' : 'pointer', fontSize: '0.875rem', padding: '0.375rem 0.75rem' }}
        onClick={() => !disabled && setShowCalendar(!showCalendar)}
      >
        <span className={value ? 'text-dark fw-normal' : 'text-muted'}>
          {formatDisplayDate(value)}
        </span>
        <i className="fas fa-calendar-alt" style={{ color: '#6c757d', fontSize: '0.8rem' }}></i>
      </div>

      {showCalendar && (
        <div 
          className="position-absolute bg-white border rounded shadow-lg p-2" 
          style={{ 
            top: '100%', 
            left: 0, 
            zIndex: 1050, 
            minWidth: '240px',
            marginTop: '2px'
          }}
        >
          {/* Calendar Header */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <button 
              type="button"
              className="btn btn-sm btn-outline-secondary"
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
              onClick={() => navigateMonth(-1)}
            >
              ‹
            </button>
            <h6 className="mb-0 fw-bold text-dark" style={{ fontSize: '0.875rem' }}>
              {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h6>
            <button 
              type="button"
              className="btn btn-sm btn-outline-secondary"
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
              onClick={() => navigateMonth(1)}
            >
              ›
            </button>
          </div>

          {/* Days of Week */}
          <div className="row g-1 mb-1">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="col text-center">
                <small className="text-dark fw-bold" style={{ fontSize: '0.7rem' }}>{day}</small>
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="row g-1">
            {getDaysInMonth(currentMonth).map((date, index) => (
              <div key={index} className="col">
                {date ? (
                  <button
                    type="button"
                    className={`btn btn-sm w-100 ${
                      isDateDisabled(date) ? 'btn-outline-light text-muted' :
                      isSelected(date) ? 'btn-primary' :
                      isToday(date) ? 'btn-outline-primary' :
                      'btn-outline-light text-dark'
                    }`}
                    onClick={() => handleDateClick(date)}
                    disabled={isDateDisabled(date)}
                    style={{ 
                      minHeight: '28px',
                      fontSize: '0.75rem',
                      color: isDateDisabled(date) ? '#6c757d' : (isSelected(date) || isToday(date) ? '' : '#212529'),
                      cursor: isDateDisabled(date) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {date.getDate()}
                  </button>
                ) : (
                  <div style={{ minHeight: '28px' }}></div>
                )}
              </div>
            ))}
          </div>

          {/* Quick Actions */}
          <div className="mt-2 d-flex gap-1">
            <button 
              type="button"
              className="btn btn-sm btn-outline-primary flex-fill"
              style={{ fontSize: '0.75rem', padding: '0.25rem' }}
              onClick={() => handleDateClick(new Date())}
              disabled={isDateDisabled(new Date())}
            >
              Today
            </button>
            <button 
              type="button"
              className="btn btn-sm btn-outline-secondary flex-fill"
              style={{ fontSize: '0.75rem', padding: '0.25rem' }}
              onClick={() => setShowCalendar(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePicker;